import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Modal,
  List,
  Popconfirm,
  Radio,
  Select,
  Space,
} from 'antd';

import { useHistory } from 'react-router-dom';
import { UploadOutlined, PlusOutlined } from '@ant-design/icons';
import { COUNTRY_OPTIONS, FRANCE, countryKey, normalizeCountry } from '../../../../modules/helpers/countries';

import _ from 'lodash';

import ContactComponent from '../../../Contacts/Contacts';
import ListComponent from '../../../Contacts/Lists';
import ExcelImport from './ExcelImport';

function RecipientsModal({ visible, onOk, onCancel }) {
  const [selectedRowKeys, setSelectedRowKeys] = useState({ contacts: [], lists: [] });
  const [selectedType, setSelectedType] = useState('contacts');
  const formValues = useSelector((state) => state.messagePayload, shallowEqual);

  const dispatch = useDispatch();

  useEffect(() => {
    setSelectedRowKeys(formValues.recipients || selectedRowKeys);
  }, [formValues.recipients]);

  return (
    <Modal
      title="Ajouter un destinataire"
      visible={visible}
      onOk={(e) => {
        dispatch({ type: 'EMAIL_FORM_SUBMITTED', payload: { recipients: selectedRowKeys } });
        onOk(e);
      }}
      onCancel={onCancel}
      width="90%"
    >
      <div style={{ float: 'left' }}>
        <Radio.Group
          onChange={(e) => setSelectedType(e.target.value)}
          style={{ marginBottom: 16 }}
          value={selectedType}
        >
          <Radio.Button value="contacts">Contacts</Radio.Button>
          <Radio.Button value="lists">Listes&nbsp;de&nbsp;diffusion</Radio.Button>
        </Radio.Group>
      </div>
      {
        selectedType === 'contacts'
          ? (
            <ContactComponent
              selectionMode
              selectedRows={selectedRowKeys[selectedType].map((s) => s && s.key)}
              onChange={(keys, rows) => {
                setSelectedRowKeys({ ...selectedRowKeys, [selectedType]: rows });
              }}
            />
          ) : (
            <ListComponent
              selectionMode
              selectedRows={selectedRowKeys[selectedType].map((s) => s && s.key)}
              onChange={(keys, rows) => {
                setSelectedRowKeys({ ...selectedRowKeys, [selectedType]: rows });
              }}
            />
          )
      }
    </Modal>
  );
}

function ManualAddModal({ visible, onOk, onCancel }) {
  const [form] = Form.useForm();

  function handleOk() {
    form.validateFields().then((values) => {
      // Pays choisi dans la liste de référence : nom canonique + code ISO (décision 07/10/2026)
      const country = normalizeCountry(values.country) || FRANCE;
      onOk({
        ...values,
        country: country.name,
        country_code: country.code,
        key: `manual_${Date.now()}`,
        type: values.company_name ? 'professional' : 'individual',
      });
      form.resetFields();
    });
  }

  return (
    <Modal
      title="Ajouter un destinataire"
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Ajouter"
      cancelText="Annuler"
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="last_name" label="Nom" rules={[{ required: true, message: 'Requis' }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="first_name" label="Prénom" rules={[{ required: true, message: 'Requis' }]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="address_line_1" label="Adresse" rules={[{ required: true, message: 'Requis' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="address_line_2" label="Complément d'adresse">
          <Input />
        </Form.Item>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="postal_code"
              label="Code postal"
              dependencies={['country']}
              rules={[
                { required: true, message: 'Requis' },
                // 5 chiffres pour la France seulement (format libre à l'étranger)
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const country = normalizeCountry(getFieldValue('country'));
                    if (!value || !country || country.code !== 'FR' || /^\d{5}$/.test(String(value).trim())) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('5 chiffres'));
                  },
                }),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item name="city" label="Ville" rules={[{ required: true, message: 'Requis' }]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="country" label="Pays" initialValue={FRANCE.name} rules={[{ required: true, message: 'Requis' }]}>
          <Select
            showSearch
            options={COUNTRY_OPTIONS}
            filterOption={(input, option) => option.search.includes(countryKey(input))}
          />
        </Form.Item>
        <Form.Item name="company_name" label="Entreprise">
          <Input />
        </Form.Item>
        <Form.Item name="reference" label="Référence">
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function Recipients({ config }) {
  const [modalVisible, setModalVisible] = useState(false);
  const [importVisible, setImportVisible] = useState(false);
  const [manualVisible, setManualVisible] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const formValues = useSelector((state) => state.messagePayload, shallowEqual);

  function clickBack() {
    history.goBack();
  }

  const allRecipients = [...formValues.recipients.contacts, ...formValues.recipients.lists];
  const hasRecipients = allRecipients.length > 0;

  function handleImport(importedRecipients) {
    const existing = formValues.recipients.contacts || [];
    dispatch({
      type: 'EMAIL_FORM_SUBMITTED',
      payload: {
        recipients: {
          ...formValues.recipients,
          contacts: [...existing, ...importedRecipients],
        },
      },
    });
    setImportVisible(false);
  }

  function handleManualAdd(recipient) {
    const existing = formValues.recipients.contacts || [];
    dispatch({
      type: 'EMAIL_FORM_SUBMITTED',
      payload: {
        recipients: {
          ...formValues.recipients,
          contacts: [...existing, recipient],
        },
      },
    });
    setManualVisible(false);
  }

  // Recommandé : import CSV + ajout manuel + carnet d'adresses
  if (config.type === 'recommande') {
    return (
      <>
        <ExcelImport
          visible={importVisible}
          onImport={handleImport}
          onCancel={() => setImportVisible(false)}
        />
        <ManualAddModal
          visible={manualVisible}
          onOk={handleManualAdd}
          onCancel={() => setManualVisible(false)}
        />

        <Card
          title="Destinataires"
          type="inner"
          style={{ width: '100%', marginTop: 16 }}
        >
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <List
                locale={{ emptyText: 'Aucun destinataire' }}
                itemLayout="horizontal"
                dataSource={allRecipients}
                renderItem={(item) => (
                  <List.Item
                    actions={[(
                      <Popconfirm
                        key="remove"
                        title="Retirer ce destinataire ?"
                        okText="Oui"
                        onConfirm={() => {
                          const type = item.list_name ? 'lists' : 'contacts';
                          dispatch({
                            type: 'EMAIL_FORM_SUBMITTED',
                            payload: {
                              recipients: {
                                ...formValues.recipients,
                                [type]: _.reject(formValues.recipients[type], { key: item.key }),
                              },
                            },
                          });
                        }}
                      >
                        <Button danger size="small">Retirer</Button>
                      </Popconfirm>
                    )]}
                  >
                    <List.Item.Meta
                      description={<RecipientDetails item={item} type={item.list_name ? 'list' : 'contact'} showAddress />}
                    />
                  </List.Item>
                )}
              />
            </Col>
          </Row>
          <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
            <Col span={24} style={{ textAlign: 'center' }}>
              <Space>
                <Button icon={<UploadOutlined />} onClick={() => setImportVisible(true)}>
                  Importer (Excel/CSV)
                </Button>
                <Button icon={<PlusOutlined />} onClick={() => setManualVisible(true)}>
                  Ajouter manuellement
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Form.Item>
              <Button onClick={clickBack} style={{ marginRight: '8px' }}>
                Précédent
              </Button>
              <Button
                onClick={() => history.push(`/envoi/${config.type}/attachments`)}
                type="primary"
                disabled={!hasRecipients}
                htmlType="submit"
              >
                Suivant
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </>
    );
  }

  // Electronique : flux existant
  return (
    <>
      <RecipientsModal
        visible={modalVisible}
        onOk={() => setModalVisible(false)}
        onCancel={() => setModalVisible(false)}
      />
      <Card
        title="Destinataires"
        type="inner"
        style={{ width: '100%', marginTop: 16 }}
      >
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <List
              locale={{ emptyText: 'Aucun destinataire' }}
              className="demo-loadmore-list"
              itemLayout="horizontal"
              dataSource={allRecipients}
              renderItem={(item) => (
                <List.Item
                  actions={[(
                    <Popconfirm
                      key="remove"
                      title={`Retirer ${item.list_name ? 'cette liste' : 'ce destinataire'} ?`}
                      okText="Oui"
                      onConfirm={() => {
                        const type = item.list_name ? 'lists' : 'contacts';
                        dispatch({
                          type: 'EMAIL_FORM_SUBMITTED',
                          payload: {
                            recipients: {
                              ...formValues.recipients,
                              [type]: _.reject(formValues.recipients[type], item),
                            },
                          },
                        });
                      }}
                    >
                      <Button danger>
                        Retirer
                      </Button>
                    </Popconfirm>
                  )]}
                >
                  <List.Item.Meta
                    description={<RecipientDetails item={item} type={item.list_name ? 'list' : 'contact'} />}
                  />
                </List.Item>
              )}
            />
          </Col>
        </Row>
        <Row gutter={[8, 8]}>
          <Col span={24} style={{ textAlign: 'center' }}>
            <Button type="primary" onClick={() => setModalVisible(true)}>Ajouter un destinataire</Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
        <Col
          span={24}
          style={{
            textAlign: 'right',
          }}
        >
          <Form.Item>
            <Button onClick={clickBack} style={{ marginRight: '8px' }}>
              Précédent
            </Button>
            <Button
              onClick={() => {
                history.push(`/envoi/${config.type}/attachments`);
              }}
              type="primary"
              disabled={!formValues.recipients.contacts.length && !formValues.recipients.lists.length}
              htmlType="submit"
            >
              Suivant
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </>
  );
}

function RecipientDetails({ type, item, showAddress }) {
  return type === 'contact'
    ? (
      <div className="contact_container">
        <div>
          <span style={{ marginBottom: 0, color: '#000', fontWeight: 600 }}>{item.first_name} {item.last_name} </span>
          {item.email && <span style={{ fontSize: '0.9em' }}>({item.email})</span>}
        </div>
        {(showAddress || !item.email) && (
          <>
            <div>{item.address_line_1}</div>
            {item.address_line_2 && (<div>{item.address_line_2}</div>)}
            <div>{item.city}, {item.postal_code}</div>
          </>
        )}
      </div>
    )
    : (
      <div className="list_container">
        <h3>{item.list_name}</h3>
        <div>{item.contacts.map((contact) => (<RecipientDetails type="contact" item={contact} key={`l${item.key}c${contact.key}`} showAddress={showAddress} />))}</div>
      </div>
    );
}
